import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

// Sub-phase U.3 — Per-route bottom action surface (phone).
//
// Pages slot their primary actions into <page-action-bar>; the bar
// renders inline (sitting where the form's old `<div class="actions">`
// would have lived) on tablet/desktop and pins itself to the bottom
// of the viewport on phone (≤ 720px). The phone presentation keeps
// the CTA in reach without the operator scrolling to find it after a
// long form (stock-edit, menu-edit) or a long list (stocktake-edit).
//
// Three default slots: left / right / (default = centre). Pages
// typically put the primary action in the right slot, secondary
// ("Cancel", "Back") in the left, and contextual chips in centre.
// The unsloted-default mirrors the natural reading order so two-button
// pages (Cancel + Save) work without explicit slot names.
//
// Behaviour:
//   Phone (≤ 720px): fixed at the bottom of the viewport, full-width,
//     elevated background. Adds bottom padding to the host so content
//     scrolled to the bottom isn't hidden under the bar (the consuming
//     page handles its own scroll container — see the host-side
//     padding-bottom: env(safe-area-inset-bottom, 0) note below).
//   Tablet / desktop: inline flex row, right-aligned content.
//
// The host adds an inset on the bottom equal to its own height + the
// safe-area-inset, so iOS notched devices stay clear of the rounded
// corners. The component uses :host(...) selectors so the consumer
// doesn't need to wrap with extra divs.
@customElement('page-action-bar')
export class PageActionBar extends LitElement {
  static styles = css`
    :host {
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: flex-end;
      padding: 8px 0;
      flex-wrap: wrap;
    }

    /* Slot ordering on the default (desktop / tablet) layout */
    ::slotted([slot='left']) {
      margin-right: auto;
    }

    @media (max-width: 720px) {
      :host {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: var(--app-surface, var(--sl-color-neutral-0));
        border-top: 1px solid var(--app-border, var(--sl-color-neutral-200));
        padding: 10px 12px;
        padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
        z-index: 20;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
      }
      :host([dark-mode-aware]) {
        background: var(--sl-color-neutral-50);
      }
    }
  `;

  render() {
    return html`
      <slot name="left"></slot>
      <slot></slot>
      <slot name="right"></slot>
    `;
  }
}
